import React, { useEffect, useRef, useState } from 'react';
import Button from '../../generic/Button.generic';
import ConfirmModal from '../../generic/ConfirmModal.generic';
import { showErrorToast, showSuccessToast } from '../../services/Utils.service';
import { download, downloadZip, list, remove, upload } from '../../services/Attachment.service';

type Attachment = {
  id: number;
  originalName: string;
  storedName: string;
  mimeType: string | null;
  size: number;
};

type Props = {
  flightId?: number;
};

const isImage = (attachment: Attachment) => (attachment.mimeType || '').startsWith('image/');

const formatSize = (bytes: number) => {
  if (!bytes) {
    return '0 o';
  }
  const units = ['o', 'Ko', 'Mo', 'Go'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, index);
  return `${index === 0 ? value : value.toFixed(1)} ${units[index]}`;
};

const errorMessage = (err: any) => {
  const data = err?.response?.data;
  if (typeof data === 'string') {
    return data;
  }
  if (data && typeof data.message === 'string') {
    return data.message;
  }
  if (data && typeof data.error === 'string') {
    return data.error;
  }
  return err?.message || 'Unexpected error';
};

const saveBlob = (data: any, filename: string) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

/** Vignette d'aperçu : l'image est récupérée via l'API authentifiée (blob). */
const AttachmentThumb = ({ flightId, attachment }: { flightId: number; attachment: Attachment }) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    download(flightId, attachment.id)
      .then((resp: any) => {
        if (!active) {
          return;
        }
        objectUrl = window.URL.createObjectURL(
          new Blob([resp.data], { type: attachment.mimeType || undefined }),
        );
        setUrl(objectUrl);
      })
      .catch(() => {
        // Pas d'aperçu possible : la ligne reste utilisable.
      });

    return () => {
      active = false;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [flightId, attachment.id, attachment.mimeType]);

  if (!url) {
    return <span className="attachments__thumb attachments__thumb--placeholder" />;
  }
  return <img className="attachments__thumb" src={url} alt={attachment.originalName} />;
};

const Attachments = ({ flightId }: Props) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [uploading, setUploading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<Attachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = () =>
    list(flightId as number).then((resp: any) => setAttachments(resp.data || []));

  useEffect(() => {
    if (!flightId) {
      return undefined;
    }
    let active = true;
    setLoading(true);
    list(flightId)
      .then((resp: any) => {
        if (active) {
          setAttachments(resp.data || []);
        }
      })
      .catch((err: any) => {
        if (active) {
          showErrorToast(errorMessage(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [flightId]);

  if (!flightId) {
    return null;
  }

  const onFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    setUploading(true);
    upload(flightId, files)
      .then(() => {
        showSuccessToast('Attachment(s) added');
        return load();
      })
      .catch((err: any) => showErrorToast(errorMessage(err)))
      .finally(() => {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      });
  };

  const onDownload = (attachment: Attachment) => {
    download(flightId, attachment.id)
      .then((resp: any) => saveBlob(resp.data, attachment.originalName))
      .catch((err: any) => showErrorToast(errorMessage(err)));
  };

  const onDownloadAll = () => {
    downloadZip(flightId)
      .then((resp: any) => saveBlob(resp.data, `flight_${flightId}.zip`))
      .catch((err: any) => showErrorToast(errorMessage(err)));
  };

  const onConfirmDelete = () => {
    if (!confirmDelete) {
      return;
    }
    const attachment = confirmDelete;
    setConfirmDelete(null);
    setDeleting(true);
    remove(flightId, attachment.id)
      .then(() => {
        showSuccessToast('Attachment deleted');
        return load();
      })
      .catch((err: any) => showErrorToast(errorMessage(err)))
      .finally(() => setDeleting(false));
  };

  return (
    <div className="attachments">
      <div className="attachments__header">
        <h3 className="attachments__title">ATTACHMENTS</h3>
        <div className="attachments__actions">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={onFilesSelected}
          />
          <Button
            label={uploading ? 'UPLOADING...' : 'ADD ATTACHMENT(S)'}
            customClass="basic_button attachments__button"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          />
          <Button
            label="DOWNLOAD ALL"
            customClass="basic_button attachments__button"
            onClick={() => {
              if (!loading && !deleting && attachments.length > 0) {
                onDownloadAll();
              }
            }}
          />
        </div>
      </div>

      {loading ? (
        <p className="attachments__empty">Loading attachments...</p>
      ) : attachments.length === 0 ? (
        <p className="attachments__empty">No attachment for this flight.</p>
      ) : (
        <ul className="attachments__list">
          {attachments.map(attachment => (
            <li key={attachment.id} className="attachments__item">
              {isImage(attachment) ? (
                <AttachmentThumb flightId={flightId} attachment={attachment} />
              ) : (
                <span className="attachments__thumb attachments__thumb--file">
                  {(attachment.originalName.split('.').pop() || 'FILE').toUpperCase().slice(0, 4)}
                </span>
              )}
              <span className="attachments__name" onClick={() => onDownload(attachment)}>
                {attachment.originalName}
                <small className="attachments__size">{formatSize(attachment.size)}</small>
              </span>
              <img
                src="img/trash_icon_red.svg"
                className="attachments__delete"
                alt="Delete attachment"
                onClick={() => setConfirmDelete(attachment)}
              />
            </li>
          ))}
        </ul>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="DELETE ATTACHMENT"
          message={`« ${confirmDelete.originalName} » will be permanently deleted.`}
          confirmLabel="DELETE"
          cancelLabel="CANCEL"
          onConfirm={onConfirmDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default Attachments;
